import isEqual from 'lodash.isequal';

const delay = (milliseconds: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
};

const compareObjects = (obj1: any, obj2: any) => isEqual(obj1, obj2);

// Función helper para verificar si un valor es undefined
const isUndefined = (value: any): value is undefined => {
  return typeof value === 'undefined';
};

// Función helper para comparar objetos usando JSON.stringify
const isEqualJson = (a: any, b: any): boolean => {
  return JSON.stringify(a) === JSON.stringify(b);
};

// Función helper para memoización simple
const memoize = <T extends (...args: any[]) => any>(
  fn: T,
  getKey?: (...args: Parameters<T>) => string
): T => {
  const cache = new Map<string, ReturnType<T>>();
  
  return ((...args: Parameters<T>): ReturnType<T> => {
    const key = getKey ? getKey(...args) : JSON.stringify(args);
    
    if (cache.has(key)) {
      return cache.get(key)!;
    }
    
    const result = fn(...args);
    cache.set(key, result);
    return result;
  }) as T;
};

// Función helper para calcular peso promedio
const calculateAverageWeight = (totalWeight: number, quantity: number): number => {
  return quantity > 0 ? totalWeight / quantity : 0;
};

// Función helper para verificar si hay datos
const hasData = (data: any[] | null | undefined): boolean => {
  return Array.isArray(data) && data.length > 0;
};

// Función helper para optimizar resize con requestAnimationFrame
const optimizedResize = (callback: () => void): void => {
  requestAnimationFrame(callback);
};

// Función helper para crear trackBy functions
const createTrackBy = <T>(key: keyof T) => {
  return (index: number, item: T): any => {
    return item[key];
  };
};

// Función helper para limpiar cache
const clearCache = (cache: Map<string, any>): void => {
  cache.clear();
};

// Función helper para validar filtros
const validateFilters = (filters: any): boolean => {
  return filters?.quantity?.min !== -1 && filters?.avgWeight?.min !== -1;
};

// Función helper para actualizaciones estables del gráfico
const stableChartUpdate = (callback: () => void, delayMs: number = 50): void => {
  // Usar setTimeout con un delay mínimo para evitar conflictos de timing
  setTimeout(callback, delayMs);
};

// Función helper para debounce de actualizaciones
const debounceChartUpdate = (callback: () => void, delayMs: number = 100): (() => void) => {
  let timeoutId: any;
  
  return () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(callback, delayMs);
  };
};

export { 
  delay, 
  compareObjects, 
  isUndefined, 
  isEqualJson,
  memoize,
  calculateAverageWeight,
  hasData,
  optimizedResize,
  createTrackBy,
  clearCache,
  validateFilters,
  stableChartUpdate,
  debounceChartUpdate
};
